import { useState, useMemo, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import Chart from "react-apexcharts";
import { fetchDataPost, fetchDataGet } from "../../controler/comunicacao";
import CardChart from "../../components/Card/Card.jsx";
import GeoMapCard from "../../components/Charts/GeoMapCard.jsx";
import ChartBarGrouped from "../../components/Charts/ChartBarGrouped.jsx";
import ChartBarStacked from "../../components/Charts/ChartBarStacked.jsx";
import ChartScatter from "../../components/Charts/ChartScatter.jsx";
import LoadingModal from "../../components/LoadingModal/LoadingModal.jsx";
import localidades from "../../components/Filter/cidades.jsx";
import "../Dashboard.css";

// ── Constantes ────────────────────────────────────────────────────────────────
const SITUACAO_OPCOES = ["Notificações", "Óbitos", "Interrupções de tratamento"];
const SITUACAO_MAP = { "Notificações": "TODOS", "Óbitos": "OBITO", "Interrupções de tratamento": "ABANDONO" };
const DEFAULT_ANOS = ["2024","2023","2022","2021","2020","2019","2018","2017","2016","2015","2014","2013","2012"];
const REGIOES_OPCOES = Object.keys(localidades);
const MAX_GEO = 3;
const STALE = 1000 * 60 * 60 * 24 * 15;

// Todos os estados (para quando nenhuma região está selecionada)
const TODOS_ESTADOS = [
  ...new Set(REGIOES_OPCOES.flatMap((r) => Object.keys(localidades[r]))),
].sort();

// ── Helpers ───────────────────────────────────────────────────────────────────
function toggleAnos(prev, ano) {
  if (ano === "TODOS") return ["TODOS"];
  const sem = prev.filter((a) => a !== "TODOS");
  if (sem.includes(ano)) {
    const next = sem.filter((a) => a !== ano);
    return next.length === 0 ? ["TODOS"] : next;
  }
  return [...sem, ano];
}

function toggleGeo(prev, item) {
  if (prev.includes(item)) return prev.filter((x) => x !== item);
  if (prev.length >= MAX_GEO) return prev;
  return [...prev, item];
}

// Paleta idêntica à usada em GeoMapCard (índice 0=azul, 1=verde, 2=âmbar)
const GEO_COLORS = ["#2563eb", "#059669", "#d97706"];

// ── Distribuição comparativa em número de casos (barras agrupadas)
const MESES_PT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

const SerieTemporalChart = memo(function SerieTemporalChart({ data }) {
  const isMensal = data.length > 0 && "MES_DIAG" in data[0];
  const xKey     = isMensal ? "MES_DIAG" : "ANO_DIAG";

  const { categories, series, colors } = useMemo(() => {
    const geos    = [...new Set(data.map((d) => d.geo))];
    const rawCats = [...new Set(data.map((d) => d[xKey]))].sort((a, b) => a - b);
    const cats    = isMensal ? rawCats.map((m) => MESES_PT[m - 1]) : rawCats.map(String);
    const srs = geos.map((geo) => ({
      name: geo,
      data: rawCats.map((cat) => {
        const r = data.find((d) => d.geo === geo && d[xKey] === cat);
        return r ? r.NumeroCasos : null;
      }),
    }));
    return { categories: cats, series: srs, colors: geos.map((_, i) => GEO_COLORS[i % GEO_COLORS.length]) };
  }, [data, xKey, isMensal]);

  const options = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false } },
    colors,
    plotOptions: { bar: { columnWidth: "65%", dataLabels: { position: "top" } } },
    dataLabels: {
      enabled: true,
      formatter: (val) => {
        if (val == null) return "";
        if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
        if (val >= 1_000)     return `${(val / 1_000).toFixed(1)}k`;
        return String(Math.round(val));
      },
      style: { fontSize: "9px", fontWeight: 700, colors: ["var(--text-50)"] },
    },
    xaxis: {
      categories,
      title: { text: isMensal ? "Mês" : "Ano de Diagnóstico", style: { color: "var(--text-50)" } },
      labels: { style: { colors: "var(--text-50)" } },
    },
    yaxis: {
      title: { text: "Número de Casos", style: { color: "var(--text-50)" } },
      labels: {
        style: { colors: "var(--text-50)" },
        formatter: (v) => v != null ? Math.round(v).toLocaleString("pt-BR") : "",
      },
    },
    legend: { labels: { colors: "var(--text-50)" } },
    tooltip: { shared: true, intersect: false },
  }), [categories, isMensal]);

  return <Chart options={options} series={series} type="bar" height="100%" width="100%" />;
});

// ── Gráfico de linhas multi-série (HIV e similares) ───────────────────────────
const MultiLineChart = memo(function MultiLineChart({ data, yKey, titleY }) {
  const isMensal = data.length > 0 && "MES_DIAG" in data[0];
  const xKey = isMensal ? "MES_DIAG" : "ANO_DIAG";

  const { categories, series, colors } = useMemo(() => {
    const geos = [...new Set(data.map((d) => d.geo))];
    const rawCats = [...new Set(data.map((d) => d[xKey]))].sort((a, b) => a - b);
    return {
      categories: isMensal ? rawCats.map((m) => MESES_PT[m - 1]) : rawCats.map(String),
      series: geos.map((geo) => ({
        name: geo,
        data: rawCats.map((cat) => {
          const row = data.find((d) => d.geo === geo && d[xKey] === cat);
          return row ? row[yKey] : null;
        }),
      })),
      colors: geos.map((_, i) => GEO_COLORS[i % GEO_COLORS.length]),
    };
  }, [data, xKey, yKey, isMensal]);

  const options = useMemo(
    () => ({
      chart: { type: "line" },
      colors,
      dataLabels: { enabled: false },
      stroke: { width: 2.5, curve: "smooth" },
      xaxis: {
        categories,
        title: { text: isMensal ? "Mês" : "Ano", style: { color: "var(--text-50)" } },
        labels: { style: { colors: "var(--text-50)" } },
      },
      yaxis: {
        title: { text: titleY, style: { color: "var(--text-50)" } },
        labels: { style: { colors: "var(--text-50)" } },
      },
      legend: { labels: { colors: "var(--text-50)" } },
      tooltip: { shared: true, intersect: false },
    }),
    [categories, isMensal, titleY, colors]
  );

  return <Chart options={options} series={series} type="line" height="100%" width="100%" />;
});

// ── Sub-componentes do filtro ─────────────────────────────────────────────────
function FilterCard({ titulo, badge, children, minWidth = 200 }) {
  return (
    <div className="card flex-shrink-0" style={{ minWidth, maxHeight: 300, overflowY: "auto" }}>
      <div className="card-body" style={{ boxShadow: "none", padding: "12px 14px" }}>
        <h6 className="card-title d-flex justify-content-between align-items-center mb-2">
          <span>{titulo}</span>
          {badge && (
            <span className="badge bg-secondary ms-1" style={{ fontSize: "0.65rem" }}>
              {badge}
            </span>
          )}
        </h6>
        {children}
      </div>
    </div>
  );
}

function RadioItem({ name, id, label, checked, onChange }) {
  return (
    <div className="form-check mb-1">
      <input className="form-check-input" type="radio" name={name} id={id} checked={checked} onChange={onChange} />
      <label className="form-check-label" htmlFor={id}>{label}</label>
    </div>
  );
}

function CheckItem({ id, label, checked, onChange, disabled }) {
  return (
    <div className="form-check mb-1">
      <input
        className="form-check-input"
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={!!disabled}
      />
      <label
        className="form-check-label"
        htmlFor={id}
        style={disabled ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
      >
        {label}
      </label>
    </div>
  );
}

// ── Painel de filtros do Comparativos ─────────────────────────────────────────
function CompFilter({ open, onComparar }) {
  const { data: anosApi } = useQuery({
    queryKey: ['anos-v2'],
    queryFn: () => fetchDataGet('/api/anos'),
    staleTime: STALE,
    refetchOnWindowFocus: false,
    retry: 0,
  });
  const ANOS_OPCOES = anosApi ?? DEFAULT_ANOS;

  const [situacao,    setSituacao]   = useState("Notificações");
  const [anos,        setAnos]       = useState(["TODOS"]);
  const [regioes,     setRegioes]    = useState([]);
  const [estados,     setEstados]    = useState([]);
  const [municipios,  setMunicipios] = useState([]);
  const [searchMunic, setSearchMunic] = useState("");

  // Estados disponíveis segundo regiões selecionadas
  const estadosDisponiveis = useMemo(() => {
    if (regioes.length === 0) return TODOS_ESTADOS;
    const uf = new Set();
    regioes.forEach((r) => Object.keys(localidades[r] || {}).forEach((e) => uf.add(e)));
    return [...uf].sort();
  }, [regioes]);

  // Municípios disponíveis segundo estados selecionados — objetos {nome, uf}
  const municipiosDisponiveis = useMemo(() => {
    const ufs = estados.length === 0 ? estadosDisponiveis : estados;
    const cids = [];
    Object.values(localidades).forEach((regiao) => {
      Object.entries(regiao).forEach(([uf, lista]) => {
        if (ufs.includes(uf)) lista.forEach((c) => cids.push({ nome: c, uf }));
      });
    });
    return cids.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [estados, estadosDisponiveis]);

  // Municípios filtrados pela busca
  const municipiosVisiveis = useMemo(() => {
    const q = searchMunic.trim().toLowerCase();
    if (!q) return municipiosDisponiveis;
    return municipiosDisponiveis.filter(
      (m) => m.nome.toLowerCase().includes(q) || m.uf.toLowerCase().includes(q)
    );
  }, [municipiosDisponiveis, searchMunic]);

  // Handlers com reset em cascata
  const handleRegioes = (item) => {
    setRegioes((prev) => toggleGeo(prev, item));
    setEstados([]);
    setMunicipios([]);
  };

  const handleEstados = (item) => {
    setEstados((prev) => toggleGeo(prev, item));
    setMunicipios([]);
    setSearchMunic("");
  };

  // Nível efetivo para comparação (mais específico com >= 2 selecionados)
  const comparaParams = useMemo(() => {
    if (municipios.length >= 2) return { nivel: "municipio", selecionados: municipios };
    if (estados.length >= 2)    return { nivel: "estado",    selecionados: estados };
    if (regioes.length >= 2)    return { nivel: "regiao",    selecionados: regioes };
    return null;
  }, [municipios, estados, regioes]);

  const nivelLabel = { regiao: "regiões", estado: "estados", municipio: "municípios" };

  const badgeRegioes = regioes.length === 0 ? "nenhuma" : `${regioes.length}/${MAX_GEO}`;
  const badgeEstados =
    estados.length === 0
      ? regioes.length > 0 ? "por região" : "nenhum"
      : `${estados.length}/${MAX_GEO}`;
  const badgeMunic =
    municipios.length === 0
      ? estados.length > 0 ? "por estado" : "nenhum"
      : `${municipios.length}/${MAX_GEO}`;

  if (!open) return null;

  return (
    <div className="container-fluid px-0 pb-3">
      <div className="d-flex overflow-auto gap-3 pb-2 align-items-start">

        {/* 1. Situação */}
        <FilterCard titulo="Situação de Desfecho">
          {SITUACAO_OPCOES.map((op) => (
            <RadioItem
              key={op}
              name="comp-situacao"
              id={`cs-${op}`}
              label={op}
              checked={situacao === op}
              onChange={() => setSituacao(op)}
            />
          ))}
        </FilterCard>

        {/* 2. Anos */}
        <FilterCard
          titulo="Anos"
          badge={anos[0] === "TODOS" ? "todos" : `${anos.length} sel.`}
        >
          <CheckItem
            id="ca-todos"
            label="TODOS"
            checked={anos[0] === "TODOS"}
            onChange={() => setAnos(["TODOS"])}
          />
          {ANOS_OPCOES.map((ano) => (
            <CheckItem
              key={ano}
              id={`ca-${ano}`}
              label={ano}
              checked={anos.includes(ano)}
              onChange={() => setAnos((prev) => toggleAnos(prev, ano))}
            />
          ))}
        </FilterCard>

        {/* 3. Regiões */}
        <FilterCard titulo="Regiões" badge={badgeRegioes}>
          {REGIOES_OPCOES.map((r) => (
            <CheckItem
              key={r}
              id={`cr-${r}`}
              label={r}
              checked={regioes.includes(r)}
              disabled={!regioes.includes(r) && regioes.length >= MAX_GEO}
              onChange={() => handleRegioes(r)}
            />
          ))}
        </FilterCard>

        {/* 4. Estados */}
        <FilterCard titulo="Estados" badge={badgeEstados}>
          {estadosDisponiveis.map((e) => (
            <CheckItem
              key={e}
              id={`ce-${e}`}
              label={e}
              checked={estados.includes(e)}
              disabled={!estados.includes(e) && estados.length >= MAX_GEO}
              onChange={() => handleEstados(e)}
            />
          ))}
        </FilterCard>

        {/* 5. Municípios */}
        <FilterCard titulo="Municípios" badge={badgeMunic} minWidth={280}>
          <div
            style={{
              position: "sticky",
              top: -12,
              zIndex: 1,
              background: "var(--card-bg, #fff)",
              paddingBottom: 8,
              marginBottom: 4,
            }}
          >
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Pesquisar município ou UF..."
              value={searchMunic}
              onChange={(e) => setSearchMunic(e.target.value)}
            />
          </div>
          {municipiosVisiveis.length === 0 && (
            <span style={{ fontSize: "0.78rem", color: "var(--text-50)" }}>
              Nenhum município encontrado.
            </span>
          )}
          {municipiosVisiveis.map((m) => {
            const key = `${m.nome}|${m.uf}`;
            return (
              <CheckItem
                key={key}
                id={`cm-${key}`}
                label={`${m.nome} — ${m.uf}`}
                checked={municipios.includes(key)}
                disabled={!municipios.includes(key) && municipios.length >= MAX_GEO}
                onChange={() => setMunicipios((prev) => toggleGeo(prev, key))}
              />
            );
          })}
        </FilterCard>

      </div>

      {/* Rodapé do filtro */}
      <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
        <span style={{ color: "var(--text-50)", fontSize: "0.85rem" }}>
          {comparaParams
            ? `Comparando ${comparaParams.selecionados.length} ${nivelLabel[comparaParams.nivel]}: ${comparaParams.selecionados.join(", ")}`
            : "Selecione pelo menos 2 regiões, estados ou municípios para comparar."}
        </span>
        <button
          className="btn btn-sm btn-success ms-auto"
          disabled={!comparaParams}
          onClick={() =>
            onComparar({
              ...comparaParams,
              anos,
              situacao: SITUACAO_MAP[situacao],
            })
          }
        >
          Comparar
        </button>
        {(regioes.length > 0 || estados.length > 0 || municipios.length > 0) && (
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => { setRegioes([]); setEstados([]); setMunicipios([]); }}
          >
            Limpar seleção
          </button>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ComparativosInteligentes() {
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [params, setParams]           = useState(null);
  const [dismissedLoading, setDismissedLoading] = useState(false);

  const body = useMemo(
    () =>
      params
        ? {
            nivel:      params.nivel,
            selecionados: params.selecionados,
            anos:       params.anos,
            situacao:   params.situacao,
          }
        : null,
    [params]
  );

  const enabled  = !!params;
  const queryKey = params
    ? [params.nivel, params.situacao, params.anos.join(","), ...params.selecionados.slice().sort()]
    : [];

  const serieQ      = useQuery({ queryKey: ["comp-serie-v2",      ...queryKey], queryFn: () => fetchDataPost("/api/comparativo/serie-temporal", body), enabled, staleTime: STALE, refetchOnWindowFocus: false });
  const desfechosQ  = useQuery({ queryKey: ["comp-desfechos",     ...queryKey], queryFn: () => fetchDataPost("/api/comparativo/desfechos",      body), enabled, staleTime: STALE, refetchOnWindowFocus: false });
  const demograficoQ= useQuery({ queryKey: ["comp-demografico",   ...queryKey], queryFn: () => fetchDataPost("/api/comparativo/demografico",    body), enabled, staleTime: STALE, refetchOnWindowFocus: false });
  const hivQ        = useQuery({ queryKey: ["comp-hiv",           ...queryKey], queryFn: () => fetchDataPost("/api/comparativo/hiv",            body), enabled, staleTime: STALE, refetchOnWindowFocus: false });
  const sexoQ       = useQuery({ queryKey: ["comp-sexo",          ...queryKey], queryFn: () => fetchDataPost("/api/comparativo/sexo",           body), enabled, staleTime: STALE, refetchOnWindowFocus: false });
  const racaQ       = useQuery({ queryKey: ["comp-raca",          ...queryKey], queryFn: () => fetchDataPost("/api/comparativo/raca",           body), enabled, staleTime: STALE, refetchOnWindowFocus: false });
  const vulnerQ     = useQuery({ queryKey: ["comp-vulneravel",    ...queryKey], queryFn: () => fetchDataPost("/api/comparativo/vulneravel",     body), enabled, staleTime: STALE, refetchOnWindowFocus: false });
  const dispersaoQ  = useQuery({ queryKey: ["comp-dispersao",     ...queryKey], queryFn: () => fetchDataPost("/api/comparativo/dispersao",      body), enabled, staleTime: STALE, refetchOnWindowFocus: false });
  const mapaGeoQ    = useQuery({ queryKey: ["comp-mapa-geo",      ...queryKey], queryFn: () => fetchDataPost("/api/comparativo/mapa-geojson",   body), enabled, staleTime: STALE, refetchOnWindowFocus: false });

  const anyLoading =
    serieQ.isLoading || desfechosQ.isLoading || demograficoQ.isLoading ||
    hivQ.isLoading   || sexoQ.isLoading       || racaQ.isLoading        ||
    vulnerQ.isLoading || dispersaoQ.isLoading || mapaGeoQ.isLoading;

  return (
    <section className="container-fluid dashboard px-0">
      <div className="titulo p-3 d-flex justify-content-between align-items-center">
        <div className="titulo-nome">Comparativos Inteligentes</div>
        <button className="filter-button" onClick={() => setFiltersOpen((o) => !o)}>
          Filtros
        </button>
      </div>

      <div className="px-3 pt-3">
        <CompFilter
          open={filtersOpen}
          onComparar={(p) => {
            setParams(p);
            setDismissedLoading(false);
            setFiltersOpen(false);
          }}
        />
      </div>

      <LoadingModal
        show={enabled && anyLoading && !dismissedLoading}
        onHide={() => setDismissedLoading(true)}
      />

      {params && !anyLoading && (
        <div className="row gx-3 gy-3 mb-3 px-3">

          {/* Mapas por localidade — um card por área selecionada */}
          {mapaGeoQ.data?.length > 0 && (
            <div className="col-12">
              <div className={`row g-3 row-cols-1 row-cols-md-${mapaGeoQ.data.length}`}>
                {mapaGeoQ.data.map((mapa, idx) => (
                  <div key={mapa.label} className="col">
                    <GeoMapCard
                      label={mapa.label}
                      geojson={mapa.geojson}
                      data={mapa.data}
                      total={mapa.total}
                      paletteIndex={idx}
                      situacao={mapa.situacao}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Distribuição comparativa */}
          <CardChart
            title="Distribuição comparativa em número de casos"
            tooltipText="Número de casos por período para cada localidade selecionada."
            modalId="SerieComp"
            loading={serieQ.isLoading}
            cols="col-12 col-lg-6"
            chartData={serieQ.data ?? null}
          >
            {serieQ.data?.length > 0 && (
              <SerieTemporalChart data={serieQ.data} />
            )}
          </CardChart>

          {/* Série temporal — Incidência (só no modo anual) */}
          {serieQ.data?.some((d) => d.Incidencia != null) && (
            <CardChart
              title="Série Temporal de Incidência (por 100 mil hab.)"
              tooltipText="Evolução da taxa de incidência de TB por 100 mil habitantes para cada localidade selecionada."
              modalId="IncidenciaComp"
              loading={serieQ.isLoading}
              cols="col-12 col-lg-6"
              chartData={serieQ.data ?? null}
            >
              <MultiLineChart
                data={serieQ.data.filter((d) => d.Incidencia != null)}
                yKey="Incidencia"
                titleY="Incidência / 100 mil hab."
              />
            </CardChart>
          )}

          {/* TB-HIV */}
          <CardChart
            title="Taxa de Coinfecção TB-HIV (%)"
            tooltipText="Percentual de casos de TB com HIV positivo por ano de diagnóstico."
            modalId="HIVComp"
            loading={hivQ.isLoading}
            cols="col-12 col-lg-6"
            chartData={hivQ.data ?? null}
          >
            {hivQ.data?.length > 0 && (
              <MultiLineChart data={hivQ.data} yKey="TaxaHIV" titleY="Taxa HIV (%)" />
            )}
          </CardChart>

          {/* Desfechos */}
          <CardChart
            title="Distribuição de Desfechos de Tratamento"
            tooltipText="Comparativo dos desfechos de tratamento (% de casos) entre localidades."
            modalId="DesfechosComp"
            loading={desfechosQ.isLoading}
            cols="col-12 col-lg-6"
            chartData={desfechosQ.data ?? null}
          >
            {desfechosQ.data?.length > 0 && (
              <ChartBarGrouped
                data={desfechosQ.data}
                categoryKey="geo"
                categorie="Desfecho"
                valueKey="NumeroCasos"
                percentageKey="Porcentagem"
                titleX="Localidade"
                titleY="% Casos"
                showPercentage={true}
              />
            )}
          </CardChart>

          {/* Perfil demográfico */}
          <CardChart
            title="Perfil Demográfico por Faixa Etária"
            tooltipText="Distribuição percentual de casos por faixa etária em cada localidade."
            modalId="DemograficoComp"
            loading={demograficoQ.isLoading}
            cols="col-12 col-lg-6"
            chartData={demograficoQ.data ?? null}
          >
            {demograficoQ.data?.length > 0 && (
              <ChartBarStacked
                data={demograficoQ.data}
                categoryKey="geo"
                categorie="FAIXA_ET"
                valueKey="NumeroCasos"
                percentageKey="Porcentagem"
                titleX="Localidade"
                titleY="% Casos"
                showPercentage={true}
              />
            )}
          </CardChart>

          {/* Por Sexo / Por Raça / Populações Vulneráveis */}
          <CardChart
            title="Distribuição por Sexo (%)"
            tooltipText="Distribuição percentual de casos de TB por sexo em cada localidade."
            modalId="SexoComp"
            loading={sexoQ.isLoading}
            cols="col-12 col-lg-4"
            chartData={sexoQ.data ?? null}
          >
            {sexoQ.data?.length > 0 && (
              <ChartBarGrouped
                data={sexoQ.data}
                categoryKey="geo"
                categorie="CS_SEXO"
                valueKey="NumeroCasos"
                percentageKey="Porcentagem"
                titleX="Localidade"
                titleY="% Casos"
                showPercentage={true}
              />
            )}
          </CardChart>

          <CardChart
            title="Distribuição por Raça/Cor (%)"
            tooltipText="Distribuição percentual de casos de TB por raça/cor em cada localidade."
            modalId="RacaComp"
            loading={racaQ.isLoading}
            cols="col-12 col-lg-4"
            chartData={racaQ.data ?? null}
          >
            {racaQ.data?.length > 0 && (
              <ChartBarGrouped
                data={racaQ.data}
                categoryKey="geo"
                categorie="CS_RACA"
                valueKey="NumeroCasos"
                percentageKey="Porcentagem"
                titleX="Localidade"
                titleY="% Casos"
                showPercentage={true}
              />
            )}
          </CardChart>

          <CardChart
            title="Populações Vulneráveis (% do total)"
            tooltipText="Percentual de casos de TB em populações vulneráveis sobre o total de casos de cada localidade."
            modalId="VulnerComp"
            loading={vulnerQ.isLoading}
            cols="col-12 col-lg-4"
            chartData={vulnerQ.data ?? null}
          >
            {vulnerQ.data?.length > 0 && (
              <ChartBarGrouped
                data={vulnerQ.data}
                categoryKey="geo"
                categorie="POP_TIPO"
                valueKey="NumeroCasos"
                percentageKey="Porcentagem"
                titleX="Localidade"
                titleY="% do total de casos"
                showPercentage={true}
              />
            )}
          </CardChart>

          {/* Dispersão PIB × Incidência */}
          <CardChart
            title="Dispersão: PIB per Capita × Média de Casos"
            tooltipText="Relação entre o PIB per capita médio e a média de casos nos últimos 5 anos."
            modalId="DispersaoComp"
            loading={dispersaoQ.isLoading}
            cols="col-12"
            chartData={dispersaoQ.data ?? null}
          >
            {dispersaoQ.data?.length > 0 && (
              <ChartScatter
                data={dispersaoQ.data}
                xKey="PIBPerCapita"
                yKey="IncidenciaMedia"
                labelKey="geo"
                titleX="PIB per Capita (R$)"
                titleY="Média de Casos"
              />
            )}
          </CardChart>

        </div>
      )}
    </section>
  );
}
